import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, Zap, Vote } from 'lucide-react';
import { FLAVORS, FLAVOR_LIST, VARIANTS, ASSETS, NUTRITION, ALLERGEN, SPECS, MIN_ORDER_QTY } from '../lib/products';
import PackImg from '../components/PackImg';
import KrispButton from '../components/KrispButton';
import Footer from '../sections/Footer';

export default function ProductPage() {
  const { slug } = useParams();
  const f = FLAVORS[slug] || FLAVOR_LIST[0];
  const navigate = useNavigate();
  const variant = '50g';
  const [qty, setQty] = useState(3);
  const PACK = VARIANTS[0];
  const price = PACK.price * qty;

  return (
    <main data-testid={`product-page-${f.slug}`} className="bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-8">
        <p className="font-cond text-xs tracking-[0.3em] text-cocoa/60">
          <Link to="/" className="hover:text-cocoa">PROKRISP</Link> / <Link to="/products" className="hover:text-cocoa">SHOP</Link> / <span style={{ color: f.color }}>{f.name}</span>
        </p>

        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 mt-6 items-start">
          <motion.div initial={{ opacity: 0, x: -40, rotate: -4 }} animate={{ opacity: 1, x: 0, rotate: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="md:sticky md:top-28 rounded-[2rem] border-2 border-cocoa p-8 relative overflow-hidden" style={{ background: f.tint }}>
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-50" style={{ background: f.color }} />
            <div className="absolute inset-0 opacity-[0.06] bg-[repeating-linear-gradient(135deg,transparent_0_26px,#2A1010_26px_28px)]" />
            <div data-cursor="expand" data-cursor-color={f.color} className="relative">
              <motion.div whileHover={{ scale: 1.05, rotate: -2 }} transition={{ type: 'spring', stiffness: 200, damping: 18 }} className="flex justify-center">
                <PackImg f={f} variant={variant} className="w-full max-h-[480px]" testid="product-image" />
              </motion.div>
            </div>
            <div className="relative flex justify-center gap-2 mt-6 flex-wrap">
              {SPECS.map((s) => <span key={s} className="rounded-full bg-cocoa text-kyellow px-3 py-1.5 text-[9px] font-black tracking-[0.2em] flex items-center gap-1"><Zap size={9} className="fill-kyellow" />{s}</span>)}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}>
            <span className="inline-block rounded-full px-3 py-1 text-[10px] font-black tracking-[0.25em] text-cocoa" style={{ background: f.color }}>NOW LIVE · 50G</span>
            <h1 className="font-display text-7xl sm:text-8xl leading-[0.85] text-cocoa mt-4">
              PROKRISP<br /><span style={{ color: f.color }}>{f.name}</span>
            </h1>
            <p className="font-cond font-bold text-xl mt-4 tracking-wider" style={{ color: f.color }}>{f.tagline}</p>
            <p className="text-sm text-cocoa/70 mt-1 font-cond tracking-[0.2em]">{f.hook}</p>

            <div className="mt-8 rounded-2xl border-2 border-cocoa p-4 flex items-center justify-between gap-3 shadow-[4px_4px_0_#2A1010]" style={{ background: f.color }}>
              <span>
                <span className="font-display text-3xl leading-none block text-cocoa">50G · ₹{PACK.price}</span>
                <span className="font-cond text-[10px] tracking-[0.25em] block mt-1 text-cocoa/80">{PACK.sub} · MIN ORDER {MIN_ORDER_QTY} PACKETS</span>
              </span>
              <span className="w-12 h-12 rounded-xl bg-cocoa text-kyellow flex flex-col items-center justify-center leading-none shrink-0"><Zap size={12} className="fill-kyellow" /><span className="font-display text-base">{PACK.protein}</span></span>
            </div>
            <div className="flex items-center gap-6 mt-6">
              <div className="flex items-center gap-4 rounded-full border-2 border-cocoa bg-white px-4 py-2.5">
                <button data-testid="qty-minus" data-cursor="plus" onClick={() => setQty(Math.max(MIN_ORDER_QTY, qty - 1))}><Minus size={14} /></button>
                <span className="font-display text-2xl w-8 text-center" data-testid="qty-value">{String(qty).padStart(2, '0')}</span>
                <button data-testid="qty-plus" data-cursor="plus" onClick={() => setQty(Math.min(99, qty + 1))}><Plus size={14} /></button>
              </div>
              <p className="font-display text-4xl leading-none" data-testid="product-price">₹{price}</p>
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <KrispButton testid="product-add-to-cart" color={f.color} className="flex-1" onClick={() => navigate(`/checkout?add=${f.slug}&qty=${qty}`)}>ADD TO CART</KrispButton>
              <KrispButton testid="product-buy-now" variant="dark" className="flex-1" onClick={() => navigate(`/checkout?add=${f.slug}&qty=${qty}`)}>BUY NOW →</KrispButton>
            </div>

            <div className="mt-12 rounded-[2rem] bg-white/60 border-2 border-cocoa overflow-hidden">
              <Block title="WHAT'S INSIDE" color={f.color}>
                <p className="text-sm text-cocoa/75 leading-relaxed">A {PACK.sub.toLowerCase()} of roasted multigrain chips packing {PACK.protein} of plant protein. {f.tagline} Real ingredients, real progress.</p>
              </Block>
              <Block title="WHY PROKRISP" color={f.color}>
                <p className="text-sm text-cocoa/75 leading-relaxed">High protein. Roasted & plant based. No palm oil. No artificial colours. Built for gym bags, game nights, study desks and everything in between.</p>
              </Block>
              <Block title="NUTRITION (PER 50G)" color={f.color}>
                <div className="rounded-xl border-2 border-cocoa/15 overflow-hidden">
                  {NUTRITION.map(([k, val]) => (
                    <div key={k} className="flex justify-between px-4 py-2 text-xs border-b border-cocoa/10 last:border-0 odd:bg-cream/60">
                      <span className="text-cocoa/60">{k}</span><span className="font-bold">{val}</span>
                    </div>
                  ))}
                </div>
              </Block>
              <Block title="INGREDIENTS" color={f.color} last>
                <p className="text-sm text-cocoa/75 leading-relaxed">{f.ingredients}</p>
                <p className="text-[10px] tracking-[0.15em] text-cocoa/50 mt-3 font-bold">ALLERGEN INFO: {ALLERGEN}</p>
              </Block>
            </div>
          </motion.div>
        </div>

        <div className="mt-20">
          <p className="font-display text-4xl text-cocoa">MORE FROM THE LINEUP</p>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {FLAVOR_LIST.filter((x) => x.slug !== f.slug).map((x) => (
              <Link key={x.slug} to={`/product/${x.slug}`} data-testid={`more-${x.slug}`} className="card-pop rounded-2xl border-2 border-cocoa p-4 flex items-center gap-4" style={{ background: x.tint }}>
                <PackImg f={x} variant={variant} className="h-24" />
                <div><span className="font-display text-2xl text-cocoa block leading-none">{x.name}</span><span className="font-cond text-[10px] tracking-[0.25em] text-cocoa/60">₹50 · 50G · 15G PROTEIN</span></div>
              </Link>
            ))}
            <Link to="/vote" data-testid="more-flavour-3" className="card-pop rounded-2xl border-2 border-cocoa bg-cocoa text-cream p-4 flex items-center gap-4">
              <img src={ASSETS.soon} alt="Coming soon" className="h-24 object-contain" />
              <div><span className="font-display text-2xl block leading-none">FLAVOUR #3 · COMING SOON</span><span className="font-cond text-[10px] tracking-[0.25em] text-kyellow flex items-center gap-1 mt-1"><Vote size={11} /> STAY TUNED · VOTE NOW</span></div>
            </Link>
          </div>
        </div>
      </div>
      <div className="mt-24"><Footer /></div>
    </main>
  );
}

const Block = ({ title, color, children, last }) => (
  <div className={`px-6 py-5 ${last ? '' : 'border-b-2 border-cocoa/10'}`}>
    <p className="font-cond font-bold text-xs tracking-[0.3em] mb-2" style={{ color }}>{title}</p>
    {children}
  </div>
);
